import type { AnalyticsExportSamplePolicy } from '@/lib/analytics-export'
import type {
  AnalyticsExportRequestRangeId,
  AnalyticsExportRequestRow,
  AnalyticsExportRequestScopeId,
} from '@/lib/analytics-export-request'
import type { AnalyticsExportResponsePreview } from '@/lib/analytics-export-response-preview'

export type AnalyticsExportAuditEventType = 'analytics_export_previewed'
export type AnalyticsExportAuditEventRequestKind = 'aggregate_export'
export type AnalyticsExportAuditEventPersistedState =
  | 'not_persisted_mock_safe'
  | 'pending_d1_audit_history'

export type AnalyticsExportAuditEventPreviewInput = {
  responsePreview: AnalyticsExportResponsePreview
  actorScope: AnalyticsExportRequestScopeId
  requestKind: AnalyticsExportAuditEventRequestKind
  providerState: string
  persistedState: AnalyticsExportAuditEventPersistedState
}

export type AnalyticsExportAuditEventPreviewPayload = {
  eventType: AnalyticsExportAuditEventType
  actorScope: AnalyticsExportRequestScopeId
  requestKind: AnalyticsExportAuditEventRequestKind
  acceptedRange: AnalyticsExportRequestRangeId
  effectiveScope: AnalyticsExportRequestScopeId
  rowCount: number
  redactionPolicy: readonly AnalyticsExportSamplePolicy[]
  providerState: string
  persistedState: AnalyticsExportAuditEventPersistedState
}

export type AnalyticsExportAuditEventPreview = {
  payload: AnalyticsExportAuditEventPreviewPayload
  rows: AnalyticsExportRequestRow[]
  mockSafe: true
}

const forbiddenAuditEventPreviewKeys = new Set([
  'answer',
  'answers',
  'rawanswer',
  'rawanswers',
  'sample',
  'samples',
  'rawsample',
  'rawsamples',
  'auditrow',
  'auditrows',
  'rawauditrow',
  'rawauditrows',
  'rawrow',
  'rawrows',
  'rawresponserow',
  'rawresponserows',
  'exportrow',
  'exportrows',
  'email',
  'contactemail',
  'phone',
  'contactphone',
  'mobile',
  'address',
  'companyaddress',
  'taxid',
  'tax_id',
  'companytaxid',
  'businessaccountingno',
  'providerpayload',
  'd1rows',
  'rawd1rows',
])

const unsafeAuditEventPreviewValuePatterns = [
  /[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}/i,
  /09\d{8}/,
  /0\d{1,2}-?\d{6,8}(?:#?\d+)?/,
  /[\u4e00-\u9fff]{0,8}(市|縣).{0,24}(路|街|巷|弄|號)/,
  /(^|[^\d])\d{8}([^\d]|$)/,
]

export function buildAnalyticsExportAuditEventPreview(
  input: AnalyticsExportAuditEventPreviewInput,
): AnalyticsExportAuditEventPreview {
  assertSafeAnalyticsExportAuditEventPreview(input)

  const payload: AnalyticsExportAuditEventPreviewPayload = {
    eventType: 'analytics_export_previewed',
    actorScope: input.actorScope,
    requestKind: input.requestKind,
    acceptedRange: input.responsePreview.payload.acceptedRange,
    effectiveScope: input.responsePreview.payload.effectiveScope,
    rowCount: input.responsePreview.payload.rowCount,
    redactionPolicy: input.responsePreview.payload.redactionPolicy,
    providerState: input.providerState,
    persistedState: input.persistedState,
  }

  return {
    mockSafe: true,
    payload,
    rows: [
      { field: 'eventType', value: 'analytics_export_previewed（mock-safe 預覽事件）' },
      {
        field: 'actorScope',
        value: getActorScopeLabel(input.responsePreview, input.actorScope),
      },
      { field: 'requestKind', value: getRequestKindLabel(input.requestKind) },
      {
        field: 'acceptedRange',
        value: getResponsePreviewRowValue(input.responsePreview, 'acceptedRange'),
      },
      {
        field: 'effectiveScope',
        value: getResponsePreviewRowValue(input.responsePreview, 'effectiveScope'),
      },
      { field: 'rowCount', value: String(input.responsePreview.payload.rowCount) },
      {
        field: 'redactionPolicy',
        value: input.responsePreview.payload.redactionPolicy.join(' + '),
      },
      { field: 'providerState', value: getProviderStateLabel(input.providerState) },
      { field: 'persistedState', value: getPersistedStateLabel(input.persistedState) },
      {
        field: 'mock-safe 說明',
        value: 'audit event preview，不查 D1，不寫入 audit table，不寫入匯出檔',
      },
    ],
  }
}

function getResponsePreviewRowValue(
  responsePreview: AnalyticsExportResponsePreview,
  field: string,
) {
  const rowValue = responsePreview.rows.find((row) => row.field === field)?.value
  if (rowValue) return rowValue
  if (field === 'acceptedRange') return responsePreview.payload.acceptedRange
  if (field === 'effectiveScope') return responsePreview.payload.effectiveScope
  return ''
}

function getActorScopeLabel(
  responsePreview: AnalyticsExportResponsePreview,
  actorScope: AnalyticsExportRequestScopeId,
) {
  if (actorScope === responsePreview.payload.effectiveScope) {
    return getResponsePreviewRowValue(responsePreview, 'effectiveScope')
  }
  return `${actorScope}（mock-safe actor scope）`
}

function getRequestKindLabel(requestKind: AnalyticsExportAuditEventRequestKind) {
  if (requestKind === 'aggregate_export') return 'aggregate_export（聚合統計匯出）'
  return requestKind
}

function getProviderStateLabel(providerState: string) {
  if (providerState === 'provider_not_configured') {
    return 'provider_not_configured（未啟用正式 provider）'
  }
  return `${providerState}（mock-safe provider state）`
}

function getPersistedStateLabel(persistedState: AnalyticsExportAuditEventPersistedState) {
  if (persistedState === 'pending_d1_audit_history') {
    return 'pending_d1_audit_history（正式 D1 audit history 待實作）'
  }
  return 'not_persisted_mock_safe（不寫入 D1 audit history）'
}

function assertSafeAnalyticsExportAuditEventPreview(value: unknown, path = 'input') {
  if (Array.isArray(value)) {
    value.forEach((item, index) =>
      assertSafeAnalyticsExportAuditEventPreview(item, `${path}[${index}]`),
    )
    return
  }

  if (value && value instanceof Date) return

  if (value && typeof value === 'object') {
    for (const [key, nestedValue] of Object.entries(value)) {
      const normalizedKey = key.toLowerCase()
      if (normalizedKey === 'rows' && path !== 'input.responsePreview') {
        throw new Error(`Unsafe analytics export audit event preview: forbidden key ${path}.${key}`)
      }
      if (forbiddenAuditEventPreviewKeys.has(normalizedKey)) {
        throw new Error(`Unsafe analytics export audit event preview: forbidden key ${path}.${key}`)
      }
      assertSafeAnalyticsExportAuditEventPreview(nestedValue, `${path}.${key}`)
    }
    return
  }

  if (typeof value !== 'string') return

  if (unsafeAuditEventPreviewValuePatterns.some((pattern) => pattern.test(value))) {
    throw new Error(`Unsafe analytics export audit event preview: forbidden value at ${path}`)
  }
}
